// The e-sign package wizard, step 1: Template (Documents tab PR 8a;
// mockup v3 screen 6, docs/design/documents-tab-workflow.md §5).
//
// CONSUMER: internal-ui (DocumentsTab, record layout, "+ → New e-sign
// package"; and E-sign → Templates "Use now" on a record).
//
// One window with the four steps across the top: 1 Template → 2 Signers &
// order → 3 Prepare fields → 4 Review & send. This PR builds step 1. On
// "Next" the forms picked here are put on the record and today's Prepare &
// Send screen opens on them, which is steps 2 to 4 as they work now; a
// later PR (8b) splits those into their own steps.
//
// Step 1 lists the record's company's templates, HARD-FILTERED to the
// record's side on the server (a buy-side file never lists a listing
// template), grouped, with the ones whose stage fit matches the record's
// stage first. A template already used on this file says so. Picking one
// shows "what's included": each form a checkbox, an optional form
// unticked with its note, and "+ Add another form" from the same
// company's library. "No template" starts from nothing.
//
// A package is often a MIX (Nate, 2026-09-30): the contract the other
// side sent back half-signed, plus a new addendum from the library, plus
// a PDF off his computer or OneDrive. So under a template or under "No
// template", "Also in this package" adds, TO BE SIGNED and in any
// combination, documents already on this file, PDFs from the computer,
// and PDFs from OneDrive. (Different from "Attach from this record"
// below it, which sends a client-shelf file along to be READ, unsigned.)
// Nothing is saved until "Next" or "Save as draft": then the computer
// and OneDrive picks land on the file's Internal shelf (the same place
// any upload lands), and the package is the library forms first, the
// added documents in the order added, then the attachments.
//
// DECISION: save picks on "Next", not the moment they are picked. Picking
// then cancelling should leave the file as it was; uploading on pick
// would leave stray copies on the file for every change of mind. A pick
// that has been saved is swapped for the saved document in the list, so
// a "Next" that fails part-way can be retried without uploading twice.
//
// Which templates exist, their group, stage fit and optional forms are
// set in E-sign → Templates only ("Manage templates"), never here.

import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";

import {
  apiV11,
  ApiError,
  type DocumentRec,
  type EsignTemplatePackage,
  type EsignTemplateRow,
  type OneDriveItem,
  type TemplatesForRecordResponse,
} from "../../api";
import { Modal } from "../Modal";
import { OneDriveBrowserModal } from "../OneDriveBrowserModal";
import { useToast } from "../Toast";
import { filterLibraryForms } from "./library-search";
import type { PrepareDocument } from "./PrepareAndSend";

export const WIZARD_STEPS = ["Template", "Signers & order", "Prepare fields", "Review & send"] as const;

/** The steps bar, shared with the later steps' screens. */
export function WizardSteps({ current }: { current: number }) {
  return (
    <div className="pkgwiz-steps" aria-label="Steps">
      {WIZARD_STEPS.map((label, i) => (
        <span
          key={label}
          className={`pkgwiz-step${i === current ? " on" : i < current ? " done" : ""}`}
          aria-current={i === current ? "step" : undefined}
        >
          <i className="pkgwiz-n">{i + 1}</i>
          {label}
        </span>
      ))}
    </div>
  );
}

function humanStage(slug: string): string {
  const s = slug.replace(/_/g, " ");
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function sideHeading(side: "buy" | "sell" | null): string {
  return side === "buy" ? "Buy-side templates" : side === "sell" ? "Listing templates" : "Templates";
}

/** The heading a template is grouped under: its own group, else its side. */
function groupOf(pkg: EsignTemplatePackage): string {
  if (pkg.groupLabel) return pkg.groupLabel;
  return pkg.side === "buy" ? "Buyer" : pkg.side === "sell" ? "Seller" : "Templates";
}

/** Step 1's order: templates fitting the record's stage first, then by
 *  group, then by name. Exported for the render test. */
export function orderTemplatesForStage(
  packages: readonly EsignTemplatePackage[],
  stage: string,
): EsignTemplatePackage[] {
  const fits = (p: EsignTemplatePackage) => (p.stageFit ?? []).includes(stage);
  return [...packages].sort((a, b) => {
    const f = Number(fits(b)) - Number(fits(a));
    if (f !== 0) return f;
    const g = groupOf(a).localeCompare(groupOf(b));
    return g !== 0 ? g : a.name.localeCompare(b.name);
  });
}

/** Put a template's ticked forms on the record ("Use now" with a subset). */
function copyPackageForms(
  packageId: string,
  kind: "opportunity" | "transaction",
  recordId: string,
  templateIds: string[],
) {
  // biome-ignore lint/correctness/useHookAtTopLevel: apiV11.useEsignTemplatePackage is an async API call (copies the package's blanks onto the record), not a hook — the name just starts with "use".
  return apiV11.useEsignTemplatePackage(packageId, {
    entity_type: kind,
    entity_id: recordId,
    template_ids: templateIds,
  });
}

type Choice = { kind: "template"; id: string } | { kind: "none" } | null;

/** A document added to the package, to be signed, from outside the library. */
export type AddedDocument =
  | { key: string; source: "file"; doc: DocumentRec }
  | { key: string; source: "computer"; file: File }
  | { key: string; source: "onedrive"; item: OneDriveItem };

/** Same ceiling as every other upload on the Documents tab. */
const MAX_UPLOAD_BYTES = 150 * 1024 * 1024;

function isPdfName(name: string, mime: string | null | undefined): boolean {
  return mime === "application/pdf" || name.toLowerCase().endsWith(".pdf");
}

/** Documents on this file that can be added to be signed: PDFs, not
 *  archived, not already fully signed (a sealed document is finished;
 *  sending it again would mean signing a signed copy), and not already in
 *  the package, whether added or attached. Exported for the render test. */
export function packageableFileDocuments(
  docs: readonly DocumentRec[],
  added: readonly AddedDocument[],
  attachedIds: readonly string[] = [],
): DocumentRec[] {
  const taken = new Set([...attachedIds, ...added.flatMap((a) => (a.source === "file" ? [a.doc.id] : []))]);
  return docs.filter(
    (d) =>
      !d.archived && isPdfName(d.filename, d.mime_type) && d.signed_status !== "fully_signed" && !taken.has(d.id),
  );
}

function addedName(a: AddedDocument): string {
  return a.source === "file" ? a.doc.filename : a.source === "computer" ? a.file.name : a.item.name;
}

function addedWhere(a: AddedDocument): string {
  if (a.source === "file") return a.doc.origin === "received" ? "On this file · from the other side" : "On this file";
  return a.source === "computer" ? "From your computer · saved to this file on Next" : "From OneDrive · saved to this file on Next";
}

let addedSeq = 0;
function nextKey(): string {
  addedSeq += 1;
  return `added-${addedSeq}`;
}

function shortDate(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

export interface PackageWizardProps {
  open: boolean;
  onClose: () => void;
  kind: "opportunity" | "transaction";
  recordId: string;
  /** "2286 E Sagebrush Ct — Okafor" for the eyebrow; optional. */
  recordLabel?: string | null;
  /** Start with this template selected (Templates "Use now"). */
  initialPackageId?: string | null;
  /** Start with this one library form ticked, no template (a checklist item
   *  tied to a single form, e.g. an AAR form). Ignored when a template is. */
  initialFormId?: string | null;
  /** The forms are on the record: open Prepare & Send on them. */
  onReady: (documents: PrepareDocument[], companySlug: string) => void;
  /** The record's own PDFs, offered as ATTACHMENTS (PR 8c): files that go
   *  out with the package to be read, never signed. */
  recordDocuments?: readonly { id: string; filename: string; document_type?: string | null; created_at?: string | null }[];
  /** "Save as draft" on step 1 (PR 8c) put a draft on the record. */
  onDraftSaved?: () => void;
}

export function PackageWizard({
  open,
  onClose,
  kind,
  recordId,
  recordLabel = null,
  initialPackageId = null,
  initialFormId = null,
  onReady,
  recordDocuments = [],
  onDraftSaved,
}: PackageWizardProps) {
  const toast = useToast();
  const [data, setData] = useState<TemplatesForRecordResponse | null>(null);
  const [library, setLibrary] = useState<EsignTemplateRow[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [choice, setChoice] = useState<Choice>(null);
  /** The forms ticked, in order (template ids). */
  const [ticked, setTicked] = useState<string[]>([]);
  /** Forms added with "+ Add another form" (template ids). */
  const [extras, setExtras] = useState<string[]>([]);
  const [adding, setAdding] = useState(false);
  const [libraryQuery, setLibraryQuery] = useState("");
  const [busy, setBusy] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** Record files ticked to ride along unsigned, in the order ticked. */
  const [attached, setAttached] = useState<string[]>([]);
  /** Documents added to be signed, from this file, the computer or OneDrive, in order. */
  const [added, setAdded] = useState<AddedDocument[]>([]);
  /** The documents already on this file, for "From this file". */
  const [fileDocs, setFileDocs] = useState<DocumentRec[]>([]);
  const [pickingFileDoc, setPickingFileDoc] = useState(false);
  const [oneDriveOpen, setOneDriveOpen] = useState(false);
  const computerInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setData(null);
    setLoadError(null);
    setError(null);
    setAdding(false);
    setExtras([]);
    setAttached([]);
    setAdded([]);
    setPickingFileDoc(false);
    setFileDocs([]);
    // "From this file" is a convenience: if the list can't load, the rest
    // of the wizard still works, so a failure here only empties it.
    Promise.resolve()
      .then(() => apiV11.listDocumentsForEntity(kind, recordId))
      .then((resp) => {
        if (!cancelled) setFileDocs(resp?.items ?? []);
      })
      .catch(() => undefined);
    Promise.all([
      apiV11.listTemplatesForRecord(kind, recordId),
      apiV11.listEsignTemplates().catch(() => ({ templates: [] as EsignTemplateRow[] })),
    ])
      .then(([resp, lib]) => {
        if (cancelled) return;
        setData(resp);
        setLibrary(lib.templates.filter((t) => t.companySlug === resp.record.company && !t.archivedAt));
        const initial = initialPackageId
          ? resp.packages.find((p) => p.id === initialPackageId)
          : undefined;
        const initialForm = initialFormId
          ? lib.templates.find(
              (t) => t.id === initialFormId && t.companySlug === resp.record.company && !t.archivedAt,
            )
          : undefined;
        if (initial) pick(initial);
        else if (initialForm) {
          // One form, no template: the same state as "No template" with the
          // form already ticked.
          setChoice({ kind: "none" });
          setTicked([initialForm.id]);
        } else setChoice(null);
      })
      .catch((err: unknown) => {
        if (!cancelled) setLoadError(err instanceof Error ? err.message : "Couldn't load templates.");
      });
    return () => {
      cancelled = true;
    };
  }, [open, kind, recordId, initialPackageId, initialFormId]);

  const ordered = useMemo(
    () => (data ? orderTemplatesForStage(data.packages, data.record.stage) : []),
    [data],
  );
  const usedAt = useMemo(() => {
    const m = new Map<string, string>();
    for (const u of data?.used ?? []) m.set(u.package_id, u.last_used_at);
    return m;
  }, [data]);
  const libraryById = useMemo(() => new Map(library.map((t) => [t.id, t])), [library]);
  const filteredLibrary = useMemo(
    () => filterLibraryForms(library, libraryQuery, ticked),
    [library, libraryQuery, ticked],
  );
  const selectedPkg =
    choice?.kind === "template" ? (data?.packages.find((p) => p.id === choice.id) ?? null) : null;

  function pick(pkg: EsignTemplatePackage) {
    setChoice({ kind: "template", id: pkg.id });
    setExtras([]);
    setAdding(false);
    // Always-needed forms start ticked; an optional one starts unticked.
    setTicked(
      pkg.items.filter((i) => !i.optional && !i.templateArchived && i.templateName).map((i) => i.esignTemplateId),
    );
  }

  function pickNone() {
    setChoice({ kind: "none" });
    setTicked([]);
    setExtras([]);
    setAdding(false);
  }

  function toggle(id: string) {
    setTicked((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  }

  /** The forms to put on the record, in package order then extras. */
  const chosenIds = useMemo(() => {
    if (choice?.kind === "none") return ticked;
    if (!selectedPkg) return [];
    const inPackage = selectedPkg.items.map((i) => i.esignTemplateId).filter((id) => ticked.includes(id));
    return [...inPackage, ...extras.filter((id) => ticked.includes(id))];
  }, [choice, selectedPkg, ticked, extras]);

  /** "Checks off when complete" (PR 11): the file's document items
   *  still to come whose type a ticked form carries. A signed copy files
   *  under its form's type, and the checklist counts it by that type. */
  const checksOff = useMemo(() => {
    const toCollect = data?.checklist_to_collect ?? [];
    if (toCollect.length === 0) return [];
    const types = new Set<string>();
    for (const id of chosenIds) {
      const type =
        libraryById.get(id)?.documentType ??
        selectedPkg?.items.find((i) => i.esignTemplateId === id)?.templateDocumentType ??
        null;
      if (type) types.add(type);
    }
    const seen = new Set<string>();
    return toCollect.filter((t) => {
      const key = `${t.title}\u0000${t.checklist_name}`;
      if (!types.has(t.document_definition_id) || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [data, chosenIds, libraryById, selectedPkg]);

  /** Everything to be signed: library forms plus added documents. */
  const total = chosenIds.length + added.length;

  function addComputerFiles(list: FileList | null) {
    const files = Array.from(list ?? []);
    const refused: string[] = [];
    const ok: AddedDocument[] = [];
    for (const file of files) {
      if (!isPdfName(file.name, file.type)) refused.push(`${file.name} is not a PDF`);
      else if (file.size === 0) refused.push(`${file.name} is empty`);
      else if (file.size > MAX_UPLOAD_BYTES) refused.push(`${file.name} is over 150 MB`);
      else ok.push({ key: nextKey(), source: "computer", file });
    }
    setAdded((prev) => [...prev, ...ok]);
    setError(refused.length > 0 ? `Not added: ${refused.join("; ")}.` : null);
  }

  /** Save one pick onto the file, returning it as a document on the file. */
  async function saveToFile(a: AddedDocument): Promise<DocumentRec> {
    if (a.source === "file") return a.doc;
    if (a.source === "computer") {
      return (await apiV11.uploadDocumentSharePoint(a.file, { entity_type: kind, entity_id: recordId })).document;
    }
    return (
      await apiV11.copyOneDriveItemToEntity({
        onedrive_item_id: a.item.id,
        entity_type: kind,
        entity_id: recordId,
      })
    ).document;
  }

  /** Copy the ticked forms onto the record, save the added documents to
   *  it, and add the attachments after them: the package's documents, in
   *  signing order. */
  async function putFormsOnRecord(): Promise<PrepareDocument[]> {
    const docs =
      chosenIds.length === 0
        ? []
        : choice?.kind === "template" && selectedPkg
          ? (await copyPackageForms(selectedPkg.id, kind, recordId, chosenIds)).documents
          : (await apiV11.assembleEsignRecordPackage(kind, recordId, chosenIds)).documents;
    // One at a time, in list order, so the package reads the way the list
    // reads and a failure names the document it stopped on.
    const saved: DocumentRec[] = [];
    for (const a of added) {
      let doc: DocumentRec;
      try {
        doc = await saveToFile(a);
      } catch (err) {
        const why = err instanceof ApiError || err instanceof Error ? err.message : "it didn't save";
        throw new Error(`Couldn't add ${addedName(a)} to the file: ${why}`);
      }
      saved.push(doc);
      // Saved now: a retry must not upload it a second time.
      if (a.source !== "file") {
        setAdded((prev) => prev.map((p) => (p.key === a.key ? { key: a.key, source: "file", doc } : p)));
      }
    }
    const byId = new Map(recordDocuments.map((d) => [d.id, d]));
    return [
      ...docs.map((d) => ({
        documentId: d.document_id,
        filename: d.filename,
        documentType: d.document_type,
        contentFingerprint: d.content_fingerprint,
      })),
      ...saved.map((d) => ({
        documentId: d.id,
        filename: d.filename,
        documentType: d.document_type ?? null,
        // A document the other side wrote earns the page-by-page look
        // before it can be sent (shared/esign/page-confirmation.ts).
        ...(d.origin === "received" ? { origin: "received" } : {}),
      })),
      ...attached.flatMap((id) => {
        const d = byId.get(id);
        return d
          ? [{ documentId: d.id, filename: d.filename, documentType: d.document_type ?? null, attachment: true }]
          : [];
      }),
    ];
  }

  async function next() {
    if (!data || total === 0) return;
    setBusy(true);
    setError(null);
    try {
      onReady(await putFormsOnRecord(), data.record.company);
    } catch (err) {
      setError(err instanceof ApiError || err instanceof Error ? err.message : "Couldn't start the package.");
    } finally {
      setBusy(false);
    }
  }

  /** "Save as draft" on step 1: the forms go on the file (as Next would
   *  put them), and the package waits on the tab until someone opens it. */
  async function saveDraft() {
    if (!data || total === 0) return;
    setSavingDraft(true);
    setError(null);
    try {
      const documents = await putFormsOnRecord();
      await apiV11.createEsignDraft(kind, recordId, {
        documents: documents.map((d) => ({
          documentId: d.documentId,
          filename: d.filename,
          documentType: d.documentType ?? null,
          origin: d.origin ?? null,
          attachment: d.attachment === true,
        })),
        // Nothing past step 1 yet: opening it starts the send screen fresh.
        state: { v: 1, startedOnly: true },
      });
      toast.success("Saved as a draft. It's on the Documents tab, under the packages.");
      onDraftSaved?.();
      onClose();
    } catch (err) {
      setError(err instanceof ApiError || err instanceof Error ? err.message : "Couldn't save the draft.");
    } finally {
      setSavingDraft(false);
    }
  }

  const fromFile = packageableFileDocuments(fileDocs, added, attached);
  const addedFileIds = new Set(added.flatMap((a) => (a.source === "file" ? [a.doc.id] : [])));
  const addable = library.filter(
    (t) =>
      !(selectedPkg?.items ?? []).some((i) => i.esignTemplateId === t.id) && !extras.includes(t.id),
  );
  const side = data?.record.side ?? null;
  const otherSideWord = side === "buy" ? "Seller" : side === "sell" ? "Buyer" : null;

  return (
    <>
    <Modal
      open={open}
      // While the OneDrive picker is open on top, its Escape and clicks
      // are its own: closing it must not also close the package.
      onClose={oneDriveOpen ? () => undefined : onClose}
      title="Start from a template"
      className="pkgwiz-modal"
      data-testid="package-wizard"
      footer={
        <div className="pkgwiz-foot">
          <button
            type="button"
            className="doc-link-btn pkgwiz-draft"
            disabled={busy || savingDraft || total === 0}
            onClick={() => void saveDraft()}
          >
            {savingDraft ? "Saving…" : "Save as draft"}
          </button>
          <span className="pkgwiz-foot-hint">
            {otherSideWord && (data?.hidden_other_side ?? 0) > 0
              ? `${otherSideWord} templates are not shown on a ${side === "buy" ? "buy-side" : "listing"} file. `
              : null}
            <Link to="/esign/templates" className="doc-link-btn">
              Manage templates
            </Link>
          </span>
          <span className="pkgwiz-foot-r">
            <button type="button" className="doc-btn" onClick={onClose}>
              Cancel
            </button>
            <button
              type="button"
              className="doc-btn doc-btn--primary"
              disabled={busy || savingDraft || total === 0}
              title={total === 0 ? "Pick a template or add at least one document." : undefined}
              onClick={() => void next()}
            >
              {busy ? "Putting the documents on the file…" : "Next: Signers & fields"}
            </button>
          </span>
        </div>
      }
    >
      <div className="pkgwiz-eyebrow">New e-sign package{recordLabel ? ` · ${recordLabel}` : ""}</div>
      <WizardSteps current={0} />
      {loadError ? (
        <p className="req-error" role="alert">
          {loadError}
        </p>
      ) : null}
      {!data && !loadError ? <p className="req-hint">Loading templates…</p> : null}
      {data ? (
        <div className="pkgwiz-body">
          <div className="pkgwiz-l">
            <div className="pkgwiz-sec">
              {sideHeading(side)}
              {data.packages.some((p) => (p.stageFit ?? []).includes(data.record.stage)) ? (
                <small>Fits {humanStage(data.record.stage)} first</small>
              ) : null}
            </div>
            <div className="pkgwiz-cards" role="listbox" aria-label="Templates">
              {ordered.map((pkg) => {
                const on = choice?.kind === "template" && choice.id === pkg.id;
                const fits = (pkg.stageFit ?? []).includes(data.record.stage);
                const used = usedAt.get(pkg.id);
                const count = pkg.items.length;
                return (
                  <button
                    key={pkg.id}
                    type="button"
                    role="option"
                    aria-selected={on}
                    className={`pkgwiz-card${on ? " on" : ""}`}
                    onClick={() => pick(pkg)}
                  >
                    {on ? <span className="pkgwiz-ok" aria-hidden /> : null}
                    <span className="pkgwiz-grp">{groupOf(pkg)}</span>
                    <span className="pkgwiz-nm">{pkg.name}</span>
                    <span className="pkgwiz-mt">
                      {count} document{count === 1 ? "" : "s"}
                      {fits ? ` · fits ${humanStage(data.record.stage)}` : ""}
                      {used ? " · already used on this file" : ""}
                    </span>
                  </button>
                );
              })}
              <button
                type="button"
                role="option"
                aria-selected={choice?.kind === "none"}
                className={`pkgwiz-card pkgwiz-card--none${choice?.kind === "none" ? " on" : ""}`}
                onClick={pickNone}
              >
                <span className="pkgwiz-nm">No template</span>
                <span className="pkgwiz-mt">Library forms, this file, your computer or OneDrive</span>
              </button>
            </div>
            {data.packages.length === 0 ? (
              <p className="req-hint">
                No templates for this kind of file yet. Pick forms under "No template", or set some up in
                Manage templates.
              </p>
            ) : null}
          </div>

          <div className="pkgwiz-r">
            {choice === null ? (
              <p className="req-hint">Pick a template to see what's in it.</p>
            ) : null}

            {selectedPkg ? (
              <>
                <div className="pkgwiz-sec">{selectedPkg.name} · what's included</div>
                <p className="req-hint pkgwiz-lede">Uncheck anything this package doesn't need.</p>
                {selectedPkg.items.map((item) => {
                  const gone = item.templateArchived || !item.templateName;
                  return (
                    <label key={item.id} className={`pkgwiz-inc${gone ? " is-gone" : ""}`}>
                      <input
                        type="checkbox"
                        checked={ticked.includes(item.esignTemplateId)}
                        disabled={gone}
                        onChange={() => toggle(item.esignTemplateId)}
                      />
                      <span>
                        <span className="pkgwiz-inc-nm">{item.templateName ?? "A form no longer in the library"}</span>
                        <span className="pkgwiz-inc-mt">
                          {gone
                            ? "Archived out of the library. Fix it in Manage templates."
                            : item.optional
                              ? item.conditionNote || "Only sometimes needed"
                              : [item.templatePublisher, item.templateRevisionLabel].filter(Boolean).join(" · ")}
                        </span>
                      </span>
                    </label>
                  );
                })}
                {extras.map((id) => {
                  const t = libraryById.get(id);
                  return (
                    <label key={id} className="pkgwiz-inc">
                      <input type="checkbox" checked={ticked.includes(id)} onChange={() => toggle(id)} />
                      <span>
                        <span className="pkgwiz-inc-nm">{t?.name ?? "Form"}</span>
                        <span className="pkgwiz-inc-mt">Added for this package</span>
                      </span>
                    </label>
                  );
                })}
                {adding ? (
                  <select
                    className="req-inp pkgwiz-add"
                    aria-label="Add another form"
                    defaultValue=""
                    onChange={(e) => {
                      const id = e.target.value;
                      if (!id) return;
                      setExtras((prev) => [...prev, id]);
                      setTicked((prev) => [...prev, id]);
                      setAdding(false);
                    }}
                  >
                    <option value="" disabled>
                      Pick a form from the library…
                    </option>
                    {addable.map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.name}
                      </option>
                    ))}
                  </select>
                ) : addable.length > 0 ? (
                  <button type="button" className="doc-link-btn" onClick={() => setAdding(true)}>
                    + Add another form
                  </button>
                ) : null}
              </>
            ) : null}

            {choice?.kind === "none" ? (
              <>
                <div className="pkgwiz-sec">
                  Pick forms from the library
                  {ticked.length > 0 ? <small>{ticked.length} picked</small> : null}
                </div>
                {library.length > 0 ? (
                  <input
                    type="search"
                    className="req-inp pkgwiz-search"
                    placeholder="Search forms, e.g. commission"
                    value={libraryQuery}
                    onChange={(e) => setLibraryQuery(e.target.value)}
                    aria-label="Search the form library"
                    data-testid="pkgwiz-library-search"
                  />
                ) : null}
                {library.length === 0 ? (
                  <p className="req-hint">This company has no blank forms in its library yet.</p>
                ) : filteredLibrary.length === 0 ? (
                  <p className="req-hint">
                    No form matches "{libraryQuery}".{" "}
                    <button type="button" className="doc-link-btn" onClick={() => setLibraryQuery("")}>
                      Clear the search
                    </button>
                  </p>
                ) : (
                  filteredLibrary.map((t) => (
                    <label key={t.id} className="pkgwiz-inc">
                      <input type="checkbox" checked={ticked.includes(t.id)} onChange={() => toggle(t.id)} />
                      <span>
                        <span className="pkgwiz-inc-nm">{t.name}</span>
                        <span className="pkgwiz-inc-mt">
                          {[t.publisher, t.revisionLabel].filter(Boolean).join(" · ")}
                        </span>
                      </span>
                    </label>
                  ))
                )}
              </>
            ) : null}

            {checksOff.length > 0 ? (
              <p className="pkgwiz-checks" data-testid="pkgwiz-checks">
                <b>Checks off when complete:</b>{" "}
                {checksOff.map((t) => `${t.title} on ${t.checklist_name}`).join("; ")}.
              </p>
            ) : null}

            {choice !== null ? (
              <div className="pkgwiz-also" data-testid="pkgwiz-also">
                <div className="pkgwiz-sec">
                  Also in this package
                  <small>Signed together with the forms above</small>
                </div>
                {added.map((a) => (
                  <div key={a.key} className="pkgwiz-inc pkgwiz-inc--added">
                    <span className="pkgwiz-inc-body">
                      <span className="pkgwiz-inc-nm">{addedName(a)}</span>
                      <span className="pkgwiz-inc-mt">{addedWhere(a)}</span>
                    </span>
                    <button
                      type="button"
                      className="doc-link-btn pkgwiz-remove"
                      aria-label={`Remove ${addedName(a)}`}
                      disabled={busy || savingDraft}
                      onClick={() => setAdded((prev) => prev.filter((p) => p.key !== a.key))}
                    >
                      Remove
                    </button>
                  </div>
                ))}
                {pickingFileDoc ? (
                  <select
                    className="req-inp pkgwiz-add"
                    aria-label="Add a document from this file"
                    defaultValue=""
                    onChange={(e) => {
                      const d = fromFile.find((x) => x.id === e.target.value);
                      if (!d) return;
                      setAdded((prev) => [...prev, { key: nextKey(), source: "file", doc: d }]);
                      setPickingFileDoc(false);
                    }}
                  >
                    <option value="" disabled>
                      Pick a document on this file…
                    </option>
                    {fromFile.map((d) => (
                      <option key={d.id} value={d.id}>
                        {d.filename}
                      </option>
                    ))}
                  </select>
                ) : null}
                <div className="pkgwiz-sources">
                  <button
                    type="button"
                    className="doc-btn"
                    disabled={busy || savingDraft || fromFile.length === 0}
                    title={fromFile.length === 0 ? "No PDFs on this file that still need signing." : undefined}
                    onClick={() => setPickingFileDoc(true)}
                  >
                    From this file
                  </button>
                  <button
                    type="button"
                    className="doc-btn"
                    disabled={busy || savingDraft}
                    onClick={() => computerInputRef.current?.click()}
                  >
                    From your computer
                  </button>
                  <button
                    type="button"
                    className="doc-btn"
                    disabled={busy || savingDraft}
                    onClick={() => setOneDriveOpen(true)}
                  >
                    From OneDrive
                  </button>
                </div>
                <input
                  ref={computerInputRef}
                  type="file"
                  accept="application/pdf,.pdf"
                  multiple
                  hidden
                  data-testid="pkgwiz-computer-input"
                  onChange={(e) => {
                    addComputerFiles(e.target.files);
                    e.target.value = "";
                  }}
                />
              </div>
            ) : null}
            {choice !== null && recordDocuments.length > 0 ? (
              <>
                <div className="pkgwiz-sec pkgwiz-sec--att">Attach from this record</div>
                <p className="req-hint pkgwiz-lede">Goes out with the package to be read. Nothing on it is signed.</p>
                {recordDocuments.filter((d) => !addedFileIds.has(d.id)).map((d) => (
                  <label key={d.id} className="pkgwiz-inc pkgwiz-inc--att">
                    <input
                      type="checkbox"
                      checked={attached.includes(d.id)}
                      onChange={() =>
                        setAttached((prev) => (prev.includes(d.id) ? prev.filter((x) => x !== d.id) : [...prev, d.id]))
                      }
                    />
                    <span>
                      <span className="pkgwiz-inc-nm">Attach: {d.filename.replace(/\.pdf$/i, "")}</span>
                      <span className="pkgwiz-inc-mt">
                        From this record{d.created_at ? ` · uploaded ${shortDate(d.created_at)}` : ""}
                      </span>
                    </span>
                  </label>
                ))}
              </>
            ) : null}
            {error ? (
              <p className="req-error" role="alert">
                {error}
              </p>
            ) : null}
          </div>
        </div>
      ) : null}
    </Modal>
    <OneDriveBrowserModal
      mode="pick"
      open={oneDriveOpen}
      onClose={() => setOneDriveOpen(false)}
      onPick={(item) => {
        setOneDriveOpen(false);
        if (!isPdfName(item.name, item.mimeType)) {
          setError(`Not added: ${item.name} is not a PDF.`);
          return;
        }
        setError(null);
        setAdded((prev) => [...prev, { key: nextKey(), source: "onedrive", item }]);
      }}
    />
    </>
  );
}
